import { useEffect, useRef, useState } from 'react';

import PayFieldsConstants from '../../@shared/constants/payfields.json';
import { manageScript } from '~/modules/@shared/helpers/manageScript';

type Params = {
  fieldIds: { [key: string]: string };
  customerId: string;
  merchantId: string;
  onSuccess: (response: any) => void;
  onFailure: (error: any) => void;
};

function setPayFieldsConfig({ merchantId }: Params) {
  if (window.PayFields?.config) {
    window.PayFields.config.apiKey = import.meta.env.VITE_PAYFIELDS_API_KEY; //PayFieldsConstants.apiKey;
    window.PayFields.config.merchant = merchantId; // 't1_mer_6570f1d21be940141510a94'; //merchantId; //

    window.PayFields.config.mode = PayFieldsConstants.mode.txnToken; // txn || txnToken || token || PayFieldsConstants.mode;
    window.PayFields.config.amount = '0';
    window.PayFields.config.txnType = 'auth';
  }
}

function setPayFieldsFields({ fieldIds, customerId }: Params) {
  if (window.PayFields?.fields) {
    window.PayFields.fields = [
      {
        type: 'number',
        element: `#${fieldIds.number}`,
        values: { number: '' },
      },
      {
        type: 'cvv',
        element: `#${fieldIds.cvv}`,
        values: { cvv: '' },
      },
      {
        type: 'expiration',
        element: `#${fieldIds.expiration}`,
        values: { expiration: '' },
      },
      {
        type: 'customer_id',
        element: `#${fieldIds.customer_id}`,
        // pass a value (customer ID) to the customer_id key below
        values: {
          customer_id: customerId, //'t1_cus_65afd8c3331c29c06e8b085',
        },
      },
    ];
  }
}
function setPayFieldsCustomizations() {
  if (window.PayFields?.customizations) {
    window.PayFields.customizations = {
      style: {
        '.cvv': {
          textAlign: 'right',
        },
        '.expiration': {
          textAlign: 'left',
        },
        '.number': {
          textAlign: 'left',
        },
        '.name': {
          textAlign: 'left',
        },
        '.zip': {
          textAlign: 'left',
        },
        '.input': {
          display: 'block',
          width: '100%',
          padding: '0.6rem 1rem',
          fontSize: '1em',
          fontWeight: '400',
          backgroundColor: '#fff',
          border: '1px solid #d3d3d3',
          appearance: 'none',
          height: '1.25rem',
          borderRadius: '5px',
          maxWidth: 'calc(100% - 3em)',
        },
        '.form-error': {
          color: 'rgb(255, 0, 128)',
          borderColor: 'rgb(255, 0, 128)',
        },
        '.address-form-error': {
          color: 'rgb(0,139,139)',
        },
        '.card-icon': {
          marginRight: '1em',
          marginTop: '0.4em',
        },
      },
    };
  }
}

function setPayFieldsHandlers({ onSuccess, onFailure }: Params) {
  if (window.PayFields?.onSuccess) {
    window.PayFields.onSuccess = onSuccess;
  }
  if (window.PayFields?.onFailure) {
    window.PayFields.onFailure = onFailure;
  }
  if (window.PayFields?.onValidationFailure) {
    window.PayFields.onValidationFailure = onFailure;
  }
}

function removePayFieldsIframes() {
  let expiration = document.querySelector('iframe#payFields-iframe-expiration');
  let name = document.querySelector('iframe#payFields-iframe-name');
  let cvv = document.querySelector('iframe#payFields-iframe-cvv');
  let number = document.querySelector('iframe#payFields-iframe-number');

  expiration?.remove();
  name?.remove();
  cvv?.remove();
  number?.remove();
}

function restartPayFields({ onSuccess }: { onSuccess: Function }) {
  if (!window.PayFields?.appended) {
    window.PayFields?.appendIframe();
  } else {
    window.PayFields.addFields();
  }
  onSuccess();
}

export default function usePayFields(params: Params) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const attempt = useRef(0);
  const [submit, setSubmit] = useState<Function>(() => {});

  useEffect(() => {
    const id = setInterval(() => {
      if (window.PayFields?.appended) {
        clearInterval(id);
      }
    }, 1);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      manageScript({ url: import.meta.env.VITE_PAYFIELDS_SCRIPT_URL });
      setIsLoaded(false);
    }, 100);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    let observerIframeIntervalId: ReturnType<typeof setInterval>;
    const id = setInterval(() => {
      attempt.current++;
      if (window.PayFields) {
        attempt.current = 0;
        clearInterval(id);

        if (!isLoaded && !loading) {
          setLoading(true);
          removePayFieldsIframes();
          setTimeout(() => {
            setPayFieldsConfig(params);
            setPayFieldsCustomizations();
            setPayFieldsHandlers(params);
            setPayFieldsFields(params);
            restartPayFields({
              onSuccess: () => {
                observerIframeIntervalId = setInterval(() => {
                  if (window.PayFields?.appended) {
                    setIsLoaded(true);
                    setLoading(false);
                    setSubmit(() => () => window.PayFields?.submit());
                    clearInterval(observerIframeIntervalId);
                  }
                }, 1000);
              },
            });
          }, 10);
        }
      } else if (attempt.current >= PayFieldsConstants.maxAttempts) {
        clearInterval(id);
      }
    }, 100);
    return () => {
      clearInterval(id);
      if (observerIframeIntervalId) {
        clearInterval(observerIframeIntervalId);
      }
    };
  }, [params, isLoaded, loading]);

  return { isLoaded, submit };
}
